<template>
  <div>
    <!--    기본정보-->
    <field-select-module
      class="mt-28px"
      title="Basic Information"
      v-model="form.stdInfo"
      :board="this.board"
      :is-print="isPrint"
      field-name="Company"
      :typeslabel="fieldTypesLabel"
    />

    <!-- Risk Assessment Information -->
    <div class="mt-8">
      <h5 class="my-4"><i class="dot"></i> Risk Assessment Information</h5>
      <div class="writeGridContainer">
        <div>
          <div>Category</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.riskInfo.category }}</p>
          <ml-input v-else :readonly="board.readonly" v-model="form.riskInfo.category" />
        </div>
        <div>
          <div>Work activity/<br/>process</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.riskInfo.process }}</p>
          <ml-input v-else :readonly="board.readonly" v-model="form.riskInfo.process" />
        </div>
        <div>
          <div>Activity</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.riskInfo.activity }}</p>
          <radio-group v-else flex :items="['RA', 'NR']" v-model="form.riskInfo.activity" />
        </div>
        <div class="writeGridLineItem">
          <div>Task</div>
          <p v-if="isPrint" class="print-input flex-1">{{ form.riskInfo.task }}</p>
          <ml-input v-else :readonly="board.readonly" v-model="form.riskInfo.task" />
        </div>
      </div>
    </div>

    <div class="mt-8">
      <div class="flex justify-between items-center">
        <div><h5 class="my-4"><i class="dot"></i> Risk Assessment</h5></div>
        <div class="ml-auto">
          <vs-button color="secondary" @click="onClickAddItem()"> Add item </vs-button>
          <vs-button color="secondary" type="border" class="ml-2" @click="onClickDeleteSelection" :disabled="selectedAssessmentIds.length === 0"> Delete selection </vs-button>
        </div>
      </div>

      <table class="w-full mt-2 risk-table">
        <thead>
        <tr>
          <th rowspan="2" style="width: 1%"><vs-checkbox :checked="selectedAssessmentAll" class="i-check justify-center" @change="onClickAssessmentAll"/></th>
          <th rowspan="2">No.</th>
          <th rowspan="2" style="width: 12.85%; min-width: 200px;">Step</th>
          <th rowspan="2" style="width: 12.85%; min-width: 200px;">Equipment/material<br/>to be used</th>
          <th colspan="2">Hazards</th>
          <th colspan="3" style="border-bottom: none;">Frequency</th>
          <th rowspan="2" class="column-frequency">Consequence</th>
          <th rowspan="2" class="column-frequency">Risk ranking</th>
          <th rowspan="2" class="column-frequency">Control required</th>
        </tr>
        <tr>
          <th>Potential·Actual hazards/Type of loss</th>
          <th>Current safeguards</th>
          <th class="column-frequency">Likelihood</th>
          <th class="column-frequency">Level</th>
          <th class="column-frequency" style="border-top: none;"></th>
        </tr>
        </thead>

        <tbody>
        <tr v-for="(row, idx) of form.assessments" :key="idx">
          <td>
            <vs-checkbox class="i-check justify-center"
                         :checked="selectedAssessmentIds.includes(row.id)"
                         @change="onClickAssessmentItem($event, row.id)"/>
          </td>
          <td>{{ idx + 1 }}</td>
          <td><ml-textarea rows="5" v-model="row.step" :is-print="isPrint" /></td>
          <td><ml-textarea rows="5" v-model="row.equipment" :is-print="isPrint" /></td>
          <td><ml-textarea rows="5" v-model="row.type" :is-print="isPrint" /></td>
          <td><ml-textarea rows="5" v-model="row.safeguards" :is-print="isPrint" /></td>
          <td><number-input v-model="row.likelihood" align="center" :min="0" :max="9"  /></td>
          <td><number-input v-model="row.level" align="center" :min="0" :max="9" /></td>
          <td><number-input v-model="row.frequency" align="center" :min="0" :max="9" /></td>
          <td><number-input v-model="row.consequence" align="center" :min="0" :max="9" /></td>
          <td><number-input v-model="row.ranking" align="center" :min="0" :max="9" /></td>
          <td>
            <vs-checkbox :checked="row.controlRequired" class="i-check justify-center" @change="row.controlRequired = !row.controlRequired"/>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <p class="mt-2">※ Activity : Routine activities (RA), Non-Routine activities (NR)</p>


    <!--    파일첨부-->
    <file-upload-module id="fileList" title="Attachments" lang="en" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule";
import TableInspectMerge from "@/components/TableInspectMerge";
import DateSelector from "@/components/selector/DateSelector";
import DisasterClassSelector from "@/components/selector/DisasterClassSelector";
import CompanySelector from "@/components/selector/CompanySelector";
import SignField from "@/components/SignField.vue";
import NameWithSignatureRow from "@/components/NameWithSignatureRow.vue";
import Pagination from "@/components/Pagination.vue";
import Print from "./Print.vue";
import TimeSelector from "@/components/selector/TimeSelector.vue";
import NumberInput from "@/components/NumberInput.vue";
import RadioGroup from "@/components/radio/RadioGroup.vue";

export default {
  components: {
    RadioGroup,
    NumberInput,
    TimeSelector,
    Print,
    Pagination,
    NameWithSignatureRow,
    SignField,
    FieldSelectModule,
    FileUploadModule,
    TableInspectMerge,
    DateSelector,
    DisasterClassSelector,
    CompanySelector,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  data() {
    return {
      form: {
        stdInfo: {},
        riskInfo: {
          category: "",
          process: "",
          activity: "RA",
          task: "",
        },

        assessments: [
          {
            id: "default_item",
            step: "",
            equipment: "",
            type: "",
            safeguards: "",
            likelihood: 0,
            level: 0,
            frequency: 0,
            consequence: 0,
            ranking: 0,
            controlRequired: false,
          }
        ],

        fileList: [],
      },
      selectedAssessmentAll: false,
      // Risk Assessment 테이블에서 선택된 항목의 인덱스를 저장하는 배열
      selectedAssessmentIds: [],
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  computed: {
    fieldTypesLabel() {
      return {
        writer_name: "Writer",
        date: "Date",
      };
    }
  },
  methods: {
    onClickAddItem() {
      this.form.assessments.push({
        id: Math.random().toString(36).substring(2, 9),
        step: "",
        equipment: "",
        type: "",
        safeguards: "",
        likelihood: 0,
        level: 0,
        frequency: 0,
        consequence: 0,
        ranking: 0,
        controlRequired: false,
      });
    },
    onClickDeleteSelection() {
      const selected = this.form.assessments.filter((item) => this.selectedAssessmentIds.includes(item.id));
      this.form.assessments = this.form.assessments.filter((item) => !selected.includes(item));
      this.selectedAssessmentIds = [];
      this.selectedAssessmentAll = false;

      // 만약 아이템이 모두 삭제됐다면, 1개를 추가한다
      if (this.form.assessments.length === 0) {
        this.onClickAddItem();
      }
    },
    onClickAssessmentAll(event) {
      const checked = event.target.checked;

      this.selectedAssessmentAll = checked;
      this.selectedAssessmentIds = checked ? this.form.assessments.map((item) => item.id) : [];
    },
    onClickAssessmentItem(event, id) {
      const checked = event.target.checked;

      if (checked) {
        this.selectedAssessmentIds.push(id);
      } else {
        this.selectedAssessmentIds = this.selectedAssessmentIds.filter((item) => item !== id);
      }
      // 전체 선택 체크박스의 상태를 변경
      this.selectedAssessmentAll = this.selectedAssessmentIds.length === this.form.assessments.length;
    },
  },
};
</script>
<style scoped lang="scss">
.container-title {
  line-height: normal;
  word-break: break-word;
}
.risk-table {
  border-collapse: collapse;

  th, td {
    border: 1px solid #cdcdcd;
    padding: 16px 10px;
    text-align: center;
  }
  th {
    background-color: #f9f9f9;
    color: #3c3c3c;
    font-weight: 500;
    font-size: 14px;

    &.column-frequency {
      width: 5.14%; min-width: 80px;
    }
  }
}

.i-check::v-deep .vs-checkbox {
  margin: 0;
}
</style>

